import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import TrashIcon from "../../../assets/icons/Trash.svg";
import moment from 'moment';
import { useDispatch } from "react-redux";
import { playSong } from "../../common/actions/common.actions";
import { BiFootball } from 'react-icons/bi';
import { IoTimeOutline } from 'react-icons/io5';
import { MdSportsSoccer, MdAccessTime } from 'react-icons/md';
import { HiOutlineCalendar } from 'react-icons/hi';
import { HiOutlineTrophy } from 'react-icons/hi2';

const Content = React.memo(({ eventTime, homeTeam, awayTeam, bodyHandicap, goalHandicap, match, sendDataToParent, selectedMatches = [], fromSelectedPage = false, handleRemoveCard, isFivePercent, isBodyPage = false }) => {

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const handleSongClick = useCallback(() => {
    dispatch(playSong());
  }, [dispatch]);

  const handleClickHome = useCallback(() => {
    handleSongClick();
    sendDataToParent("home", match);
  }, [handleSongClick, sendDataToParent, match]);

  const handleClickAway = useCallback(() => {
    handleSongClick();
    sendDataToParent("away", match);
  }, [handleSongClick, sendDataToParent, match]);

  const handleClickOver = useCallback(() => {
    handleSongClick();
    sendDataToParent("over", match);
  }, [handleSongClick, sendDataToParent, match]);

  const handleClickUnder = useCallback(() => {
    handleSongClick();
    sendDataToParent("under", match);
  }, [handleSongClick, sendDataToParent, match]);

  // Single .find() replaces 5 .some() calls
  const selectedMatch = useMemo(() => {
    return selectedMatches?.find((x) => x.eventId === match.eventId);
  }, [selectedMatches, match.eventId]);

  const isSelected = !!selectedMatch;
  const isHomeChosen = isSelected && selectedMatch?.choosen === "home";
  const isAwayChosen = isSelected && selectedMatch?.choosen === "away";
  const isOverChosen = isSelected && selectedMatch?.choosen === "over";
  const isUnderChosen = isSelected && selectedMatch?.choosen === "under";

  const removeCard = useCallback(() => {
    handleSongClick();
    handleRemoveCard(match.rapidEventId);
  }, [handleSongClick, handleRemoveCard, match.rapidEventId]);

  return (
    <li className="w-full max-w-2xl mx-auto font-myanmarSansPro">
      {/* Card */}
      <div
        className={`rounded-xl shadow-lg px-2 py-2 transition-all duration-300 backdrop-blur-sm border-2 relative ${
          isBodyPage
            ? isFivePercent
              ? "bg-red-100 border-crimson/60 shadow-crimson/20"
              : "bg-gradient-to-r from-white/90 via-white/95 to-white/90 border-crimson/30"
            : "bg-gradient-to-r from-white/90 via-white/95 to-white/90 border-crimson/30 hover:border-crimson/60 hover:shadow-xl hover:bg-gradient-to-r hover:from-white/95 hover:via-white/98 hover:to-white/95"
        }`}
      >
        {/* Header with Date/Time (Left) and League Name (Right) */}
        <div className="relative flex items-center justify-between mb-3">
          {/* Time - Left */}
          <div className="flex items-center gap-1.5">
            <div className="flex items-center justify-center w-6 h-6 border rounded-lg shadow-sm bg-gradient-to-br from-crimson via-red-primary to-crimson border-golden/40">
              <MdAccessTime className="text-sm text-white" />
            </div>
            <p className="text-xs font-bold text-blue-primary">
              {moment(new Date(match?.eventTime)).format("DD MMM, h:mm A")}
            </p>
          </div>

          {/* Trash Button (if from selected page) */}
          {fromSelectedPage && (
            <button className="flex items-center justify-center w-6 h-6 transition-all duration-200 border rounded-lg cursor-pointer bg-crimson/10 hover:bg-crimson/20 border-crimson/40" onClick={removeCard}>
              <img src={TrashIcon} alt="Remove" className="w-3.5 h-3.5 filter brightness-0 saturate-100 hue-rotate-[350deg]" />
            </button>
          )}

          {/* League Name - Right */}
          <div className="flex items-center gap-1.5">
            <div className="flex items-center justify-center w-6 h-6 border rounded-lg shadow-sm bg-gradient-to-br from-crimson via-red-primary to-crimson border-golden/40">
              <HiOutlineTrophy className="text-sm text-white" />
            </div>
            <p className="text-xs font-bold text-blue-primary">
              {match.leagueName}
            </p>
          </div>

          {/* Luxury Border Accent at bottom - centered with reduced width */}
          <div className="absolute -bottom-1.5 left-1/2 transform -translate-x-1/2 w-3/4 h-0.5 bg-gradient-to-r from-crimson via-golden to-crimson shadow-sm"></div>
        </div>

        {/* Teams Selection */}
        <div
          className={`grid grid-cols-2 gap-1 mb-1 ${fromSelectedPage ? "pointer-events-none" : ""}`}
        >
          <div
            onClick={handleClickHome}
            className={`${isHomeChosen
              ? "bg-gradient-to-br from-crimson via-red-primary to-crimson text-white shadow-lg shadow-crimson/30 border-2 border-golden/50"
              : "bg-white text-blue-primary hover:bg-crimson/5 border-2 border-crimson/30 hover:border-crimson/50"
            } cursor-pointer rounded-lg px-2 flex items-center text-center justify-center transition-all duration-200 relative overflow-hidden`}
          >
            {!isHomeChosen && <div className="absolute inset-0 transition-opacity duration-300 opacity-0 bg-gradient-to-r from-crimson/5 via-golden/5 to-transparent hover:opacity-100"></div>}
            <h3 className="relative z-10 flex justify-center flex-1 ml-auto text-sm font-bold sm:text-base">
              {i18n.language === "en" ? match.homeTeam : match.homeTeamMyan}
            </h3>
            {match.overId === match.homeId && (
              <h3 className="bg-golden/95 p-1.5 font-mono rounded-lg ml-auto font-bold text-blue-primary text-xs sm:text-sm shadow-sm border border-golden/60 relative z-10">
                {match.bodyHandicap}
              </h3>
            )}
          </div>

          <div
            onClick={handleClickAway}
            className={`${isAwayChosen
              ? "bg-gradient-to-br from-crimson via-red-primary to-crimson text-white shadow-lg shadow-crimson/30 border-2 border-golden/50"
              : "bg-white text-blue-primary hover:bg-crimson/5 border-2 border-crimson/30 hover:border-crimson/50"
            } cursor-pointer rounded-lg py-2.5 px-2 flex items-center text-center justify-center transition-all duration-200 relative overflow-hidden`}
          >
            {!isAwayChosen && <div className="absolute inset-0 transition-opacity duration-300 opacity-0 bg-gradient-to-r from-crimson/5 via-golden/5 to-transparent hover:opacity-100"></div>}
            {match.overId === match.awayId && (
              <h3 className="bg-golden/95 whitespace-nowrap p-1.5 font-mono font-bold rounded-lg mr-auto text-blue-primary text-xs sm:text-sm shadow-sm border border-golden/60 relative z-10">
                {match.bodyHandicap}
              </h3>
            )}
            <h3 className="relative z-10 w-full text-sm font-bold text-center sm:text-base">
              {i18n.language === "en" ? match.awayTeam : match.awayTeamMyan}
            </h3>
          </div>
        </div>

        {/* Goal Over/Under Selection */}
        <div className={`grid grid-cols-5 gap-1 ${fromSelectedPage ? "pointer-events-none" : ""}`}>
          <div
            onClick={handleClickOver}
            className={`${isOverChosen
              ? "bg-gradient-to-br from-crimson via-red-primary to-crimson text-white shadow-lg shadow-crimson/30 border-2 border-golden/50"
              : "bg-white text-blue-primary hover:bg-crimson/5 border-2 border-crimson/30 hover:border-crimson/50"
            } cursor-pointer rounded-lg py-2 px-2 col-span-2 transition-all duration-200 relative overflow-hidden`}
          >
            {!isOverChosen && <div className="absolute inset-0 transition-opacity duration-300 opacity-0 bg-gradient-to-r from-crimson/5 via-golden/5 to-transparent hover:opacity-100"></div>}
            <h3 className="relative z-10 py-0 text-sm font-bold text-center">
              {t('goalOver')}
            </h3>
          </div>

          <div className="cursor-not-allowed text-center bg-golden/95 rounded-lg p-1.5 flex justify-center items-center shadow-sm border border-golden/60">
            <h3 className="font-mono text-xs font-bold text-center text-blue-primary sm:text-sm">
              {match.goalHandicap}
            </h3>
          </div>

          <div
            onClick={handleClickUnder}
            className={`${isUnderChosen
              ? "bg-gradient-to-br from-crimson via-red-primary to-crimson text-white shadow-lg shadow-crimson/30 border-2 border-golden/50"
              : "bg-white text-blue-primary hover:bg-crimson/5 border-2 border-crimson/30 hover:border-crimson/50"
            } cursor-pointer text-center rounded-lg py-2 px-2 col-span-2 flex justify-center items-center transition-all duration-200 relative overflow-hidden`}
          >
            {!isUnderChosen && <div className="absolute inset-0 transition-opacity duration-300 opacity-0 bg-gradient-to-r from-crimson/5 via-golden/5 to-transparent hover:opacity-100"></div>}
            <h3 className="relative z-10 text-sm font-bold text-center">
              {t('goalUnder')}
            </h3>
          </div>
        </div>
      </div>
    </li>
  );
});

export default Content;